import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Briefcase, Building, Loader2, X, Plus } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Placement() {
  const [companies, setCompanies] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("jobs");
  const [showJobModal, setShowJobModal] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [jobForm, setJobForm] = useState({ company_name: "", title: "", package_lpa: "", description: "", eligible_branches: "", deadline: "", openings: 10 });
  const [companyForm, setCompanyForm] = useState({ name: "", industry: "", website: "", contact_email: "", contact_person: "" });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [c, j] = await Promise.all([
        axios.get(`${API}/companies`, { withCredentials: true }),
        axios.get(`${API}/jobs`, { withCredentials: true }),
      ]);
      setCompanies(c.data); setJobs(j.data);
    } catch {} finally { setLoading(false); }
  };

  const handleApply = async (id) => {
    try { await axios.post(`${API}/jobs/${id}/apply`, {}, { withCredentials: true }); fetchAll(); } catch {}
  };

  const handleAddJob = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/jobs`, { ...jobForm, package_lpa: parseFloat(jobForm.package_lpa), eligible_branches: jobForm.eligible_branches.split(",").map(s => s.trim()), openings: parseInt(jobForm.openings) }, { withCredentials: true });
      setShowJobModal(false); fetchAll();
    } catch {}
  };

  const handleAddCompany = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/companies`, companyForm, { withCredentials: true });
      setShowCompanyModal(false); fetchAll();
    } catch {}
  };

  return (
    <div className="space-y-6" data-testid="placement-module">
      <div className="flex items-start justify-between border-b border-[#D8D4CB] pb-5">
        <div>
          <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#BDBAA8] mb-1">Placement Cell</p>
          <h2 className="font-['Syne'] font-bold text-2xl text-[#0C0C10]">Placement Management</h2>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowCompanyModal(true)} data-testid="add-company-btn" className="border border-[#D8D4CB] text-[#0C0C10] font-['Syne'] font-bold text-[10px] uppercase tracking-widest px-4 py-3 hover:bg-[#0C0C10] hover:text-[#EAFF00] hover:border-[#0C0C10] transition-all flex items-center gap-1.5">
            <Building className="w-3.5 h-3.5" /> Company
          </button>
          <button onClick={() => setShowJobModal(true)} data-testid="post-job-btn" className="bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-[10px] uppercase tracking-widest px-5 py-3 hover:bg-[#1C1C22] transition-colors flex items-center gap-2">
            <Briefcase className="w-3.5 h-3.5" /> Post Job
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Active Jobs", value: jobs.filter(j => j.status === "active").length, color: "#06B6D4" },
          { label: "Companies", value: companies.length, color: "#0A7AFF" },
          { label: "Total Applicants", value: jobs.reduce((s, j) => s + (j.applicants || 0), 0), color: "#8B5CF6" },
          { label: "Total Openings", value: jobs.reduce((s, j) => s + (j.openings || 0), 0), color: "#22C55E" },
        ].map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="bg-white border border-[#E2DDD4] p-5" style={{ borderLeft: `3px solid ${s.color}` }}>
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-2">{s.label}</p>
            <p className="font-['JetBrains_Mono'] font-bold text-3xl text-[#0C0C10]">{s.value}</p>
          </motion.div>
        ))}
      </div>

      <div className="flex gap-2 border-b border-[#D8D4CB]">
        {[["jobs", "Job Postings", Briefcase], ["companies", "Companies", Building]].map(([t, label, Icon]) => (
          <button key={t} onClick={() => setActiveTab(t)} data-testid={`placement-tab-${t}`}
            className={`flex items-center gap-2 font-['Syne'] font-bold text-[9px] uppercase tracking-widest px-5 py-3 border-b-2 transition-all ${activeTab === t ? "border-[#0C0C10] text-[#0C0C10]" : "border-transparent text-[#BDBAA8]"}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {activeTab === "jobs" && (
        <div className="bg-white border border-[#E2DDD4]">
          <div className="grid gap-0 px-5 py-3 border-b border-[#E2DDD4] bg-[#FAFAF7]" style={{ gridTemplateColumns: "1.5fr 1fr 80px 1fr 80px 80px 80px" }}>
            {["Job Title", "Company", "Package", "Branches", "Openings", "Applicants", "Deadline"].map(h => (
              <span key={h} className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8]">{h}</span>
            ))}
          </div>
          {loading ? <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#BDBAA8]" /></div>
            : jobs.map((j, i) => (
            <div key={j._id} className="grid gap-0 px-5 py-3.5 border-b border-[#F0ECE4] last:border-0 hover:bg-[#FAFAF7] items-center" style={{ gridTemplateColumns: "1.5fr 1fr 80px 1fr 80px 80px 80px" }}>
              <div>
                <p className="font-['Syne'] font-bold text-xs text-[#0C0C10]">{j.title}</p>
                <p className="font-['DM_Sans'] text-[10px] text-[#BDBAA8] line-clamp-1">{j.description}</p>
              </div>
              <span className="font-['DM_Sans'] text-xs text-[#0C0C10]">{j.company_name}</span>
              <span className="font-['JetBrains_Mono'] text-xs font-bold text-[#22C55E]">₹{j.package_lpa}L</span>
              <span className="font-['DM_Sans'] text-[10px] text-[#7A7468]">{Array.isArray(j.eligible_branches) ? j.eligible_branches.join(", ") : j.eligible_branches}</span>
              <span className="font-['JetBrains_Mono'] text-xs text-[#0C0C10]">{j.openings}</span>
              <span className="font-['JetBrains_Mono'] text-xs font-bold text-[#06B6D4]">{j.applicants || 0}</span>
              <span className="font-['DM_Sans'] text-[10px] text-[#BDBAA8]">{j.deadline}</span>
            </div>
          ))}
        </div>
      )}

      {activeTab === "companies" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {companies.map((c, i) => (
            <div key={c._id} className="bg-white border border-[#E2DDD4] p-5 hover:border-[#0C0C10] transition-colors">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#06B6D4]/10 border border-[#06B6D4]/20 flex items-center justify-center flex-shrink-0">
                  <span className="font-['Syne'] font-bold text-base text-[#06B6D4]">{c.name.charAt(0)}</span>
                </div>
                <div>
                  <p className="font-['Syne'] font-bold text-sm text-[#0C0C10]">{c.name}</p>
                  <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-wider text-[#BDBAA8]">{c.industry}</p>
                </div>
              </div>
              <p className="font-['DM_Sans'] text-xs text-[#7A7468] mb-1">{c.contact_person}</p>
              <p className="font-['DM_Sans'] text-xs text-[#BDBAA8]">{c.contact_email}</p>
              <div className="mt-3 pt-3 border-t border-[#F0ECE4] flex items-center justify-between">
                <span className="font-['JetBrains_Mono'] text-xs font-bold text-[#0C0C10]">{c.total_offers || 0} offers</span>
                <span className={`font-['Syne'] font-bold text-[9px] uppercase px-2 py-1 border ${c.status === "active" ? "border-[#22C55E]/30 text-[#16A34A] bg-[#22C55E]/8" : "border-[#BDBAA8]/30 text-[#BDBAA8]"}`}>{c.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {showJobModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setShowJobModal(false)}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#F5F2EA] max-w-md w-full p-8">
              <div className="flex justify-between mb-6"><h3 className="font-['Syne'] font-bold text-lg text-[#0C0C10]">Post Job</h3><button onClick={() => setShowJobModal(false)}><X className="w-5 h-5 text-[#BDBAA8]" /></button></div>
              <form onSubmit={handleAddJob} className="space-y-4">
                {[["company_name", "Company Name"], ["title", "Job Title"], ["package_lpa", "Package (LPA)"], ["description", "Description"], ["eligible_branches", "Eligible Branches (comma-separated)"], ["deadline", "Application Deadline"]].map(([f, l]) => (
                  <div key={f}><label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-1.5">{l}</label>
                  <input type={f === "deadline" ? "date" : f === "package_lpa" ? "number" : "text"} value={jobForm[f]} onChange={e => setJobForm(p => ({...p, [f]: e.target.value}))} required
                    className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] transition-colors" /></div>
                ))}
                <button type="submit" className="w-full bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-xs uppercase tracking-widest py-3.5 hover:bg-[#1C1C22] transition-colors">Post Job</button>
              </form>
            </motion.div>
          </motion.div>
        )}
        {showCompanyModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setShowCompanyModal(false)}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#F5F2EA] max-w-md w-full p-8">
              <div className="flex justify-between mb-6"><h3 className="font-['Syne'] font-bold text-lg text-[#0C0C10]">Register Company</h3><button onClick={() => setShowCompanyModal(false)}><X className="w-5 h-5 text-[#BDBAA8]" /></button></div>
              <form onSubmit={handleAddCompany} className="space-y-4">
                {[["name", "Company Name"], ["industry", "Industry"], ["website", "Website"], ["contact_email", "Contact Email"], ["contact_person", "Contact Person"]].map(([f, l]) => (
                  <div key={f}><label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-1.5">{l}</label>
                  <input type={f === "contact_email" ? "email" : "text"} value={companyForm[f]} onChange={e => setCompanyForm(p => ({...p, [f]: e.target.value}))} required
                    className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] transition-colors" /></div>
                ))}
                <button type="submit" className="w-full bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-xs uppercase tracking-widest py-3.5 hover:bg-[#1C1C22] transition-colors">Register</button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
